import type { IfcElementType } from '@editor/components/Controls/IFCUploadWidget/constants/ifcFilter'
import type { IfcElementTypeAssignment } from '@editor/components/Controls/IFCUploadWidget/types/ifcMetadata'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import type { GlbElementGeometry } from '../../utils/glbElementGeometry'

/**
 * Elements whose area-weighted face tilt (from horizontal) is at least this are walls; everything
 * below is treated as a roof slab. Walls sit near 90° even including their small top/bottom faces;
 * pitched roofs stay well below even for steep pitches.
 */
export const WALL_MIN_TILT_DEGREES = 65

/** Minimum vertical overlap (m) for another wall to count as a same-storey ray occluder. */
const OCCLUDER_MIN_Z_OVERLAP = 0.2

/** Sample offsets along the wall axis (fractions of the wall length, relative to the center). */
const RAY_SAMPLE_FRACTIONS = [-0.25, 0, 0.25]

/** A side counts as exterior when at least this many of its sample rays escape unobstructed. */
const MIN_FREE_RAYS_PER_SIDE = 2

export interface WallFootprint {
  guid: string
  /** Convex 2D footprint (CCW) of the wall in aligned model space. */
  footprint: ImmutableVector3[]
  /** Vertical extent [minZ, maxZ]; occluders from other storeys (no z-overlap) are ignored. */
  zRange: [number, number]
}

interface Point2 {
  x: number
  y: number
}

/** `true` when segment a1→a2 properly intersects segment b1→b2 (collinear grazing ignored). */
function segmentsIntersect(a1: Point2, a2: Point2, b1: Point2, b2: Point2): boolean {
  const rx = a2.x - a1.x
  const ry = a2.y - a1.y
  const sx = b2.x - b1.x
  const sy = b2.y - b1.y
  const denominator = rx * sy - ry * sx
  if (Math.abs(denominator) < 1e-12) return false
  const qpx = b1.x - a1.x
  const qpy = b1.y - a1.y
  const t = (qpx * sy - qpy * sx) / denominator
  const u = (qpx * ry - qpy * rx) / denominator
  return t >= 0 && t <= 1 && u >= 0 && u <= 1
}

/** Consecutive point pairs of a closed polygon (last edge wraps back to the first point). */
function polygonEdges(polygon: ImmutableVector3[]): [Point2, Point2][] {
  const edges: [Point2, Point2][] = []
  for (let i = 0; i < polygon.length; i++) {
    edges.push([polygon[i], polygon[(i + 1) % polygon.length]])
  }
  return edges
}

interface PreparedWall {
  guid: string
  edges: [Point2, Point2][]
  zRange: [number, number]
  /** Ray sample start points along the wall's center line. */
  samples: Point2[]
  /** Unit normal (perpendicular to the wall's long axis). Rays go along ±normal. */
  normal: Point2
}

/** Derive axis, normal, and interior sample points from a wall's convex footprint. */
function prepareWall(wall: WallFootprint): PreparedWall | null {
  const { footprint } = wall
  if (footprint.length < 3) return null

  // Wall axis = direction of the longest hull edge.
  let axis: Point2 | null = null
  let longest = 0
  for (const [a, b] of polygonEdges(footprint)) {
    const dx = b.x - a.x
    const dy = b.y - a.y
    const length = Math.hypot(dx, dy)
    if (length > longest) {
      longest = length
      axis = { x: dx / length, y: dy / length }
    }
  }
  if (!axis || longest < 1e-9) return null

  const centroid = footprint.reduce(
    (acc, p) => ({ x: acc.x + p.x / footprint.length, y: acc.y + p.y / footprint.length }),
    { x: 0, y: 0 },
  )

  // Wall length = extent of the hull projected onto the axis.
  let minT = Infinity
  let maxT = -Infinity
  for (const p of footprint) {
    const t = p.x * axis.x + p.y * axis.y
    minT = Math.min(minT, t)
    maxT = Math.max(maxT, t)
  }
  const length = maxT - minT

  return {
    guid: wall.guid,
    edges: polygonEdges(footprint),
    zRange: wall.zRange,
    samples: RAY_SAMPLE_FRACTIONS.map(f => ({
      x: centroid.x + axis.x * f * length,
      y: centroid.y + axis.y * f * length,
    })),
    normal: { x: -axis.y, y: axis.x },
  }
}

function zOverlap(a: [number, number], b: [number, number]): number {
  return Math.min(a[1], b[1]) - Math.max(a[0], b[0])
}

/**
 * Detect exterior walls by 2D ray casting: from interior sample points along each wall's center
 * line, rays are cast to both sides along the wall's normal. A side whose rays escape without
 * crossing another wall's footprint faces open air, so the wall is an outer wall. Occluders are
 * restricted to walls with overlapping z-ranges so stacked walls of other storeys don't interfere.
 */
export function computeExteriorWallGuids(walls: WallFootprint[]): Set<string> {
  const prepared = walls.map(prepareWall).filter((wall): wall is PreparedWall => wall !== null)

  // Ray length: diagonal of the overall footprint bounds, so rays always reach outside.
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const wall of prepared) {
    for (const [a] of wall.edges) {
      minX = Math.min(minX, a.x)
      minY = Math.min(minY, a.y)
      maxX = Math.max(maxX, a.x)
      maxY = Math.max(maxY, a.y)
    }
  }
  const rayLength = Math.hypot(maxX - minX, maxY - minY) + 1

  const exterior = new Set<string>()
  for (const wall of prepared) {
    const occluders = prepared.filter(
      other =>
        other.guid !== wall.guid && zOverlap(other.zRange, wall.zRange) > OCCLUDER_MIN_Z_OVERLAP,
    )

    for (const sign of [1, -1]) {
      const direction = { x: wall.normal.x * sign, y: wall.normal.y * sign }
      let freeRays = 0
      for (const sample of wall.samples) {
        const end = { x: sample.x + direction.x * rayLength, y: sample.y + direction.y * rayLength }
        const blocked = occluders.some(occluder =>
          occluder.edges.some(([a, b]) => segmentsIntersect(sample, end, a, b)),
        )
        if (!blocked) freeRays++
      }
      if (freeRays >= MIN_FREE_RAYS_PER_SIDE) {
        exterior.add(wall.guid)
        break
      }
    }
  }
  return exterior
}

export interface AutoTypeAssignmentsInput {
  /** guid -> current effective element type (only actual changes are returned). */
  guidToType: Record<string, string>
  /**
   * guid -> aligned GLB geometry of the elements to classify. The caller restricts this to the
   * elements visible in the scene; every entry is classified regardless of its current type.
   */
  guidToGeometry: Record<string, GlbElementGeometry>
}

/**
 * Classify elements purely from their geometry, assuming only walls and roof slabs are given:
 * elements dominated by vertical faces (tilt ≥ `WALL_MIN_TILT_DEGREES`) are walls and become
 * `outer_walls`/`inner_walls` via exterior ray casting (`computeExteriorWallGuids`); every other
 * element becomes `roof_slabs`. Existing (manual) type assignments are overwritten; only elements
 * whose detected type differs from their current effective type are returned.
 */
export function computeAutoTypeAssignments(
  input: AutoTypeAssignmentsInput,
): IfcElementTypeAssignment[] {
  const { guidToType, guidToGeometry } = input

  const detected: Record<string, IfcElementType> = {}
  const walls: WallFootprint[] = []

  for (const [guid, geometry] of Object.entries(guidToGeometry)) {
    if (geometry.dominantTiltDegrees >= WALL_MIN_TILT_DEGREES) {
      walls.push({
        guid,
        footprint: geometry.footprint,
        zRange: [geometry.box.min.z, geometry.box.max.z],
      })
    } else {
      detected[guid] = 'roof_slabs'
    }
  }

  const exteriorGuids = computeExteriorWallGuids(walls)
  for (const wall of walls) {
    detected[wall.guid] = exteriorGuids.has(wall.guid) ? 'outer_walls' : 'inner_walls'
  }

  return Object.entries(detected)
    .filter(([guid, elementType]) => guidToType[guid] !== elementType)
    .map(([elementId, elementType]) => ({ elementId, elementType }))
}
